import type * as Pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import type { DraftFile, FormRecordDetail, FormTemplate, StoredRecordFile } from './types.js';

/**
 * Uygulama içinde doldurulabilir PDF (plan M8b Task 17; ADR 0042 §19; ADR 0024 #20): yeni
 * bağımlılık yok, M8a3'ün pdf.js'i (`AnnotationMode.ENABLE_FORMS`, `annotationStorage`,
 * `saveDocument()`). Yalnız AcroForm; XFA ya da JavaScript eylemli PDF uygulamada doldurulmaz
 * (önizleme, Print ve Download açık kalır). Betik çalışmaz [ADR 0028 #35(4); UR E27 madde 11].
 * Düzleştirme yapılmaz: kaydedilen ve onaylanan bayt `saveDocument()` çıktısıdır; alan değerleri
 * ayrıca saklanmaz. Ofiste doldurma yok.
 */

export type PdfjsModule = typeof Pdfjs;
export type PdfFormDocument = Awaited<ReturnType<PdfjsModule['getDocument']>['promise']>;

/** Belge açılışı: değerlendirme (`eval`) ve XFA kapalı. */
export const PDF_FORM_LOAD_OPTIONS = {
  isEvalSupported: false,
  enableXfa: false,
  useWasm: false,
} as const;

/** Form katmanı çizimi: betik sandığı kurulmaz (`enableScripting`; pdf.js görüntüleyici ayarı). */
export const PDF_FORM_RENDER_OPTIONS = {
  enableScripting: false,
  renderForms: true,
} as const;

export type PdfFormBlock = 'PDF_NO_FIELDS' | 'PDF_HAS_SCRIPTS' | 'PDF_IS_XFA';

export interface PdfFormInspection {
  fillable: boolean;
  reason: PdfFormBlock | null;
  /** AcroForm alan sayısı (ad başına). */
  fields: number;
}

/** PDF'i form doldurma için açar (baytlar bellekte; şablon küçüktür). */
export async function openPdfForm(lib: PdfjsModule, data: Uint8Array): Promise<PdfFormDocument> {
  return lib.getDocument({ data, ...PDF_FORM_LOAD_OPTIONS }).promise;
}

/**
 * Fill in app'in denetimi (ADR 0042 §19; Ek U32): sunucu yalnız adaylığı verir (PDF kaynak,
 * gemide, yürürlükte); alan, XFA ve betik burada pdf.js ile denetlenir. Sıra: XFA, betik, alan.
 */
export async function inspectPdfForm(doc: PdfFormDocument): Promise<PdfFormInspection> {
  const metadata = await doc.getMetadata();
  const info = (metadata.info ?? {}) as { IsXFAPresent?: boolean };
  const fields = (await doc.getFieldObjects()) ?? new Map<string, unknown[]>();
  const count = fields.size;
  if (info.IsXFAPresent || doc.isPureXfa) {
    return { fillable: false, reason: 'PDF_IS_XFA', fields: count };
  }
  if (await doc.hasJSActions())
    return { fillable: false, reason: 'PDF_HAS_SCRIPTS', fields: count };
  if (count === 0) return { fillable: false, reason: 'PDF_NO_FIELDS', fields: 0 };
  return { fillable: true, reason: null, fields: count };
}

/** Actions'ta "Fill in app": sunucu adaylığı ve istemci denetimi birlikte (§19, §21). */
export function fillInAppVisible(
  template: Pick<FormTemplate, 'fillInApp'> | null,
  inspection: PdfFormInspection | null,
): boolean {
  return Boolean(template?.fillInApp.allowed && inspection?.fillable);
}

/** Doldurulan PDF'in baytları: `saveDocument()` çıktısı, düzleştirme yok (§19). */
export async function savePdfForm(doc: PdfFormDocument): Promise<Uint8Array> {
  return doc.saveDocument();
}

/** Kayıttaki dosyanın adı: kayıt no ve şablonun adı ("FR-2026-00042 Form 05 - Drill.pdf"). */
export function recordFileName(recordNo: string, templateName: string): string {
  return `${recordNo} ${templateName}`.replace(/[<>:"/|?*\\]/g, '_').slice(0, 255);
}

/**
 * Doldurulan PDF'i yükler ve taslağın sonuna `pdf_form` kaynağıyla ekler (ADR 0042 §1, §19):
 * gönderimde `record_revision_files.source = pdf_form` olur. Yeniden doldurulan PDF öncekinin
 * yerine geçer (çıkarılan dosya depoda kalır).
 */
export async function attachFilledPdf(input: {
  record: Pick<FormRecordDetail, 'id' | 'recordNo' | 'draft'>;
  bytes: Uint8Array;
  templateName: string;
  upload: (name: string, bytes: Uint8Array) => Promise<StoredRecordFile>;
  updateDraft: (
    recordId: string,
    patch: { expectedRowVersion: number; files: DraftFile[] },
  ) => Promise<FormRecordDetail>;
}): Promise<FormRecordDetail> {
  const draft = input.record.draft;
  if (!draft) throw Object.assign(new Error('RECORD_SUBMITTED'), { code: 'RECORD_SUBMITTED' });
  const stored = await input.upload(
    recordFileName(input.record.recordNo, input.templateName),
    input.bytes,
  );
  // Uygulamada doldurulan PDF kayıtta tektir: yeniden doldurulan öncekinin yerine geçer
  const others = [...draft.files]
    .filter((f) => f.source !== 'pdf_form')
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((f, index) => ({ ...f, sortOrder: index }));
  const file: DraftFile = {
    sortOrder: others.length,
    name: stored.name,
    sha256: stored.sha256,
    size: stored.size,
    mimeType: stored.mimeType,
    source: 'pdf_form',
  };
  return input.updateDraft(input.record.id, {
    expectedRowVersion: draft.rowVersion,
    files: [...others, file],
  });
}
